import { useState } from 'react';
import { StatusChip } from '../../components/data';
import { DeleteButton, EmployeePicker, ErrorBanner, Field, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import type { EosHeadline } from './eosTypes';
import { EosSection } from './eosUi';

/**
 * Headlines — one-sentence customer and employee news.
 *
 * A five-minute section, so the screen is a list and an input and nothing
 * else. Read out, then archived: archiving rather than deleting keeps last
 * week's list available without it piling up on this week's.
 */
export function EosHeadlines({ teamId, onChanged }: { teamId: number; onChanged: () => void }) {
  const headlines = useResource<EosHeadline[]>(`/eos/headlines?teamId=${teamId}`);
  const employees = useResource<Employee[]>('/employees?status=active');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<'customer' | 'employee'>('customer');
  const [ownerEmployeeId, setOwner] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    if (!title.trim()) return;
    setError(null);
    try {
      await api.post('/eos/headlines', { teamId, title: title.trim(), kind, ownerEmployeeId });
      setTitle('');
      headlines.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that headline.');
    }
  };

  const list = headlines.data ?? [];

  return (
    <>
      {error && <ErrorBanner message={error} />}

      <EosSection
        title="Headlines"
        note="One sentence each — customer and employee news. Archive them once they have been read out."
      >
        <form
          className="row wrap"
          style={{ gap: 8, alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <div className="grow">
            <Field label="New headline">
              <input
                className="input"
                placeholder="One sentence"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>
          </div>
          <Field label="About">
            <select
              className="select"
              value={kind}
              onChange={(e) => setKind(e.target.value as 'customer' | 'employee')}
            >
              <option value="customer">Customer</option>
              <option value="employee">Employee</option>
            </select>
          </Field>
          <Field label="From">
            <EmployeePicker employees={employees.data ?? []} value={ownerEmployeeId} onChange={setOwner} />
          </Field>
          <button type="submit" className="btn btn-primary" disabled={!title.trim()}>
            Add
          </button>
        </form>

        {headlines.loading && <Loading label="Loading headlines" />}
        {headlines.error && <ErrorBanner message={headlines.error} />}
        {list.length === 0 && !headlines.loading && <p className="muted mt-4">No headlines this week.</p>}

        <div className="mt-4">
          {list.map((headline) => (
            <div key={headline.HeadlineId} className="eos-issue">
              <div className="grow">
                <span>{headline.Title}</span>
                <div className="xs subtle mt-2">{headline.OwnerName ?? 'Unattributed'}</div>
              </div>
              <div className="row" style={{ gap: 4 }}>
                <StatusChip band="neutral">{headline.Kind}</StatusChip>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    void api
                      .put(`/eos/headlines/${headline.HeadlineId}`, { isArchived: true })
                      .then(() => {
                        headlines.reload();
                        onChanged();
                      })
                      .catch((err: unknown) =>
                        setError(err instanceof Error ? err.message : 'Could not archive it.'),
                      );
                  }}
                >
                  Archive
                </button>
                <DeleteButton
                  label="✕"
                  what="this headline"
                  onConfirm={() => {
                    void api
                      .del(`/eos/headlines/${headline.HeadlineId}`)
                      .then(() => {
                        headlines.reload();
                        onChanged();
                      })
                      .catch((err: unknown) =>
                        setError(err instanceof Error ? err.message : 'Could not delete it.'),
                      );
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </EosSection>
    </>
  );
}
